import { Component } from '@angular/core';
import { RxFormBuilder, RxFormGroup } from '@rxweb/reactive-form-validators';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { Filter, FilterOperation, OrFilter } from 'src/app/core/jsonapi/filter';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { FormValidatorService } from 'src/app/core/services/form-validator.service';
import { AddressesService } from 'src/app/modules/common/_services/addresses.service';
import { CountriesService } from 'src/app/modules/common/_services/countries.service';
import { ExpertsService } from 'src/app/modules/experts/_services/experts.service';
import { Expert } from 'src/app/modules/experts/_types/expert';
import { LoginService } from 'src/app/modules/login/login.service';

@Component({
    selector: 'app-create-new',
    templateUrl: './create-new.component.html',
    styleUrls: ['./create-new.component.scss'],
    standalone: false
})
export class CaseExpertCreateNewComponent {
  public expertFormGroup!: RxFormGroup;

  public expert: Expert = new Expert();

  public allowWrite: boolean = false;
  public allowDelete: boolean = false;

  public countries: any = [];

  constructor(
    private expertsService: ExpertsService,
    private addressesService: AddressesService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private formBuilder: RxFormBuilder,
    private formValidationService: FormValidatorService,
    private countriesService: CountriesService,
    private dynamicDialogRef: DynamicDialogRef,
    private loginService: LoginService
  ) {
    this.expertFormGroup = this.formBuilder.formGroup<Expert>(this.expert) as RxFormGroup;
  }

  async ngOnInit() {
    this.loadCountries();

    this.allowWrite = this.loginService.hasPermission("CASES.WRITE");
    this.allowDelete = this.loginService.hasPermission("CASES.DELETE");
  }

  async loadCountries() {
    let params: IParamsCollection = {
      sort: ['name']
    };
    let response = await firstValueFrom(this.countriesService.all(params));
    this.countries = response.data;
  }

  async save() {
    let valid = this.formValidationService.validateForm(this.expertFormGroup);
    if (valid) {
      this.expert = Object.assign(this.expert, this.expertFormGroup.value);

      let hasDuplicatedExpert = await this.hasDuplicatedExpert();
      if (hasDuplicatedExpert) {
        this.confirmationService.confirm({
          message: "Ya existe un perito con el mismo nombre, teléfono o email. ¿Desea crearlo igualmente?",
          header: "Perito duplicado",
          acceptLabel: "Crear nuevo",
          rejectLabel: "Cancelar",
          icon: 'pi pi-exclamation-triangle',
          accept: async () => {
            await this.persistExpert();
          },
          reject: () => {
            this.dynamicDialogRef.close();
          }
        });
        return;
      }

      await this.persistExpert();

    } else {
      this.messageService.add({ closable: true, severity: 'error', summary: "Error", detail: "Existen errores, por favor revise el formulario de datos." });
    }
  }

  private async hasDuplicatedExpert(): Promise<boolean> {
    let name = this.expert.name?.trim();
    let phone1 = this.expert.phone1?.trim();
    let email = this.expert.email?.trim();

    let duplicateFilters: any[] = [];
    if (name) {
      duplicateFilters.push(new Filter(FilterOperation.equals, 'name', '', name));
    }
    if (phone1) {
      duplicateFilters.push(new Filter(FilterOperation.equals, 'phone1', '', phone1));
    }
    if (email) {
      duplicateFilters.push(new Filter(FilterOperation.equals, 'email', '', email));
    }

    if (duplicateFilters.length === 0) {
      return false;
    }

    let filters = {
      duplicatedExpert: new OrFilter(duplicateFilters)
    };

    let params: IParamsCollection = {
      page: { number: 1, size: 1 },
      filter: FilterBuilder.fromObject(filters).build()
    };

    let response = await firstValueFrom(this.expertsService.all(params));
    return (response.meta?.['total'] || 0) > 0;
  }

  private async persistExpert() {
    if (this.expert.address != null) {
      let address: any = await firstValueFrom(this.addressesService.save(this.expert.address));
      this.expert.addressId = address.id;
    }

    let expertItem = await firstValueFrom(this.expertsService.save(this.expert));
    this.messageService.add({ closable: true, severity: 'success', summary: "Éxito", detail: "Perito guardado correctamente." });

    this.dynamicDialogRef.close(expertItem);
  }

  cancel() {
    this.dynamicDialogRef.close();
  }
}
